import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { EMPTY_GUID, isEmptyGuid } from '@/shared/lib/empty-guid';
import type { CodeDisplayName } from '@/shared/types/billing';

/**
 * Service catalogue for the invoice "Add service" picker. A local copy of pm-react
 * `src/billing/services/use-invoice-services.ts` (`useServiceFees`, PROD-1006 / PROD-2368): there
 * is no shared package, so the contract and the behaviour are mirrored here.
 *
 *     POST /api/Billing/Services/Fees?pageNumber&pageSize[&q]
 *     body { providerId, locationId, patientId, type: "provider" | "all" }
 *     → { results: [{ id, procedureCode, shortDescription, fees: [{ id, fee, location }] }] }
 */

export interface ServiceFeeLocation {
  id?: string;
  name?: string;
  description?: string | null;
}
export interface ServiceFee {
  id?: string;
  fee?: number;
  location?: ServiceFeeLocation;
}
export interface ServiceFeeResult {
  id?: string;
  procedureCode?: CodeDisplayName;
  shortDescription?: string;
  fees?: ServiceFee[];
}
interface ServiceFeesResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ServiceFeeResult[];
}

/** "provider" = only this provider's fee-schedule services; "all" = the full org catalogue. */
export type ServiceFeesType = 'provider' | 'all';

export interface ServiceFeesParams {
  providerId?: string;
  locationId?: string;
  patientId?: string;
  type?: ServiceFeesType;
  /** Free-text search, sent as `q` (a server-side search, not a client filter). */
  query?: string;
}

/**
 * Can the backend answer this parameter set? `all` needs only a patient; `provider` also needs a
 * REAL provider — `Guid.Empty` is rejected with 400 "ProviderId cannot be empty when Type is
 * Provider" (PROD-2368). Exported so the picker can tell "zero rows" from "no request was made".
 */
export function canFetchServiceFees({ providerId, patientId, type = 'provider' }: ServiceFeesParams): boolean {
  if (!patientId) return false;
  if (type === 'provider' && isEmptyGuid(providerId)) return false;
  return true;
}

/**
 * Services the provider can bill. `enabled` is the picker's `open`, so it fetches only while the
 * dialog is up. No staleTime and refetch on every mount: fees can change between two opens
 * (pm-react, Andrew 2026-08-25); `keepPreviousData` keeps the rows up while a new search settles.
 */
export function useServiceFees(params: ServiceFeesParams, enabled = true) {
  const { providerId, locationId, patientId, type = 'provider', query } = params;
  const q = query?.trim() || undefined;
  // `''` and `undefined` both mean "no provider"; .NET binds `Guid.Empty` but rejects `''`.
  const provider = providerId || EMPTY_GUID;
  return useQuery({
    queryKey: ['billing', 'service-fees', provider, locationId, patientId, type, q],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ pageNumber: '1', pageSize: '50' });
      if (q) qs.set('q', q);
      return post<ServiceFeesResponse>(
        `/api/Billing/Services/Fees?${qs.toString()}`,
        { providerId: provider, locationId, patientId, type },
        { signal },
      );
    },
    enabled: enabled && canFetchServiceFees({ providerId, patientId, type }),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
